import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Box, Paper, Menu, MenuItem, ListItemIcon, Typography } from "@mui/material";
import { Settings, NewReleases, StarBorder, Archive, Logout, Delete, Reply, Forward, Info } from "@mui/icons-material";
import { useSocket } from "../../context/socketContext";
import { useSelector } from "react-redux";
import { selectAuthData } from "../../core/store/redux/slices/Auth-slice";
import ChatList from "./whatsapp/chatList";
import ChatWindow from "./whatsapp/chatWindow";
import NameEditDialog from "./whatsapp/NameEditDialog";
import MediaPreview from "./whatsapp/MediaPreview";
import { END_POINT } from "../../utils/End-points";
import ApiClient from "../../apis/Api-client";
import { errorToster, successToster } from "../../shared/toster/Toster";
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';

const MAX_FILE_SIZE = 16 * 1024 * 1024;

const normalizePhoneNumber = (number) => {
  if (!number) return '';
  const cleaned = number.toString().replace(/\D/g, '');
  return cleaned.startsWith('91') ? `+${cleaned}` : `+91${cleaned}`;
};

// Display format: HH:MM only
const formatDisplayTime = (date) => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Precise format: HH:MM:SS for internal ordering
const formatPreciseTime = (date) => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const processMessage = (rawMessage) => {
  const {
    message_id, customer_number, message, inbound, message_type = "text",
    status = "delivered", timestamp, time, isMe, senderName, s3_url,
    media_id, media_filename, media_size, temp_id
  } = rawMessage;

  const normalizedNumber = normalizePhoneNumber(customer_number);
  const messageDate = new Date(timestamp || Date.now());
  const displayTime = time || formatDisplayTime(messageDate);
  const preciseTime = formatPreciseTime(messageDate);
  const messageTimestamp = messageDate.getTime();

  return {
    id: message_id,
    tempId: temp_id,
    text: message || "",
    time: displayTime, 
    preciseTime: preciseTime, 
    isMe: isMe !== undefined ? isMe : !inbound,
    status: status,
    senderName: senderName || (inbound ? normalizedNumber : "You"),
    timestamp: messageTimestamp,
    messageType: message_type,
    customerNumber: normalizedNumber,
    mediaUrl: s3_url,
    mediaId: media_id,
    filename: media_filename,
    mediaSize: media_size
  };
};

const getMediaType = (mimeType) => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'document';
};

const getMediaDisplayText = (messageType, filename) => {
  const types = {
    'image': 'Photo',
    'video': 'Video',
    'audio': 'Audio',
    'document': filename ? `Document: ${filename}` : 'Document'
  };
  return types[messageType] || 'Media';
};

const WhatsAppWeb = () => {
  const socket = useSocket();
  const { hotel } = useSelector(selectAuthData);
  const { notificationApi } = ApiClient();
  const navigate = useNavigate();

  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [chats, setChats] = useState([]);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [nameEditOpen, setNameEditOpen] = useState(false);

  const loadedChatsRef = useRef(false);
  const markAsReadInProgress = useRef(new Set());
  const apiCallsInProgress = useRef(new Set());
  const messageStore = useRef(new Map());
  const isMobile = window.innerWidth < 900;

  const WhatsAppAPI = useMemo(() => ({
    async getAllMessages(hotelKey) {
      const callKey = `getAllMessages-${hotelKey}`;
      if (apiCallsInProgress.current.has(callKey)) {
        throw new Error('API call already in progress');
      }
      apiCallsInProgress.current.add(callKey);
      try {
        const res = await notificationApi.get(END_POINT.WHATSAPP.GET_ALL_CHATS, { hotel_key: hotelKey });
        if (res?.status === 200) return res.data.data;
        throw new Error('Failed to fetch chats');
      } finally {
        apiCallsInProgress.current.delete(callKey);
      }
    },

    async getCustomerMessages(hotelKey, customerNumber, page = 1) {
      const callKey = `getCustomerMessages-${hotelKey}-${customerNumber}-${page}`;
      if (apiCallsInProgress.current.has(callKey)) {
        throw new Error('API call already in progress');
      }
      apiCallsInProgress.current.add(callKey);
      try {
        const res = await notificationApi.get(END_POINT.WHATSAPP.GET_CUSTOMER_MESSAGES, {
          hotel_key: hotelKey,
          customer_number: customerNumber,
          page: page.toString(),
          limit: "20"
        });
        if (res?.status === 200) return res.data;
        throw new Error('Failed to fetch messages');
      } finally {
        apiCallsInProgress.current.delete(callKey);
      }
    },

    async uploadMedia(hotelKey, file) {
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File size exceeds 16MB limit. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      }
      const formData = new FormData();
      formData.append('hotel_key', hotelKey);
      formData.append('files', file);
      const res = await notificationApi.post(END_POINT.WHATSAPP.UPLOAD_MEDIA, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res?.status === 200) return res.data.data;
      throw new Error('Failed to upload media');
    },

    async markAsRead(hotelKey, customerNumber) {
      const callKey = `markAsRead-${hotelKey}-${customerNumber}`;
      if (markAsReadInProgress.current.has(callKey)) return;
      markAsReadInProgress.current.add(callKey);
      try {
        const res = await notificationApi.put(END_POINT.WHATSAPP.MARK_AS_READ, {
          hotel_key: hotelKey,
          customer_number: customerNumber
        });
        if (res?.status === 200) return res.data.data;
      } catch (error) {
        console.error('Mark as read failed:', error);
      } finally {
        markAsReadInProgress.current.delete(callKey);
      }
    },

    async updateCustomerName(hotelKey, customerNumber, name) {
      const res = await notificationApi.put(END_POINT.WHATSAPP.UPDATE_NAME, {
        hotel_key: hotelKey,
        customer_number: customerNumber,
        name: name
      });
      if (res?.status === 200) return res.data;
      throw new Error('Failed to update name');
    },

    async deleteMessage(hotelKey, customerNumber, message_id) {
      const res = await notificationApi.delet(END_POINT.WHATSAPP.DELETE_MESSAGE, null, {
        hotel_key: hotelKey,
        customer_number: customerNumber,
        message_id
      });
      if (res?.status === 200) return res.data;
      throw new Error('Failed to delete Message');
    }
  }), [notificationApi]);

  const sortChatsByTimestamp = useCallback((chats) => {
    return [...chats].sort((a, b) => (b.lastMessageTimestamp || 0) - (a.lastMessageTimestamp || 0));
  }, []);

  const updateChatOrder = useCallback((customerNumber, lastMessage, messageType, timestamp, isInbound) => {
    setChats(prevChats => {
      const existingChatIndex = prevChats.findIndex(chat => 
        chat.number === customerNumber || chat.normalizedNumber === customerNumber
      );
      
      const messageText = lastMessage || getMediaDisplayText(messageType, null);
      const messageTimestamp = timestamp || Date.now();
      
      if (existingChatIndex !== -1) {
        const existingChat = prevChats[existingChatIndex];
        const updatedChat = {
          ...existingChat,
          lastMessage: messageText,
          lastMessageTimestamp: messageTimestamp,
          lastMessageType: messageType,
          lastMessageInbound: isInbound,
          lastMessageStatus: isInbound ? null : 'sent'
        };
        
        const otherChats = prevChats.filter((_, index) => index !== existingChatIndex);
        return sortChatsByTimestamp([updatedChat, ...otherChats]);
      }
      
      const newChat = {
        id: customerNumber,
        name: customerNumber,
        number: customerNumber,
        normalizedNumber: normalizePhoneNumber(customerNumber),
        lastMessage: messageText,
        lastMessageTimestamp: messageTimestamp,
        lastMessageType: messageType,
        lastMessageInbound: isInbound,
        lastMessageStatus: isInbound ? null : 'sent',
        unreadCount: isInbound ? 1 : 0,
        isBlocked: false,
        messages: []
      };
      
      return sortChatsByTimestamp([newChat, ...prevChats]);
    });
  }, [sortChatsByTimestamp]);

  useEffect(() => {
    const loadChats = async () => {
      if (!hotel?.hotel_key || loadedChatsRef.current || isLoadingChats) return;
      
      loadedChatsRef.current = true;
      setIsLoadingChats(true);
      
      try {
        const chatsData = await WhatsAppAPI.getAllMessages(hotel.hotel_key);
        const processedChats = chatsData.map(chat => ({
          id: chat.id,
          name: chat.customer_name || normalizePhoneNumber(chat.customer_number),
          number: chat.customer_number,
          normalizedNumber: normalizePhoneNumber(chat.customer_number),
          lastMessage: chat.last_message || "",
          lastMessageAt: chat.last_message_at,
          lastMessageTimestamp: chat.last_message_timestamp,
          lastMessageType: chat.last_message_type || 'text',
          lastMessageInbound: chat.last_message_inbound || false,
          lastMessageStatus: chat.last_message_status || 'delivered',
          unreadCount: chat.unread_count || 0,
          isBlocked: chat.isblocked || false,
          messages: []
        }));
        
        setChats(sortChatsByTimestamp(processedChats));
      } catch (error) {
        console.error('Failed to load chats:', error);
        if (error.message !== 'API call already in progress') {
          errorToster("Failed to fetch chats");
        }
      } finally {
        setIsLoadingChats(false);
      }
    };

    loadChats();
  }, [hotel?.hotel_key, WhatsAppAPI, isLoadingChats, sortChatsByTimestamp]);

  const loadCustomerMessages = useCallback(async (customerNumber, page = 1, isNewChat = false) => {
    if (!hotel?.hotel_key || !customerNumber || isLoadingMessages) return;
    
    setIsLoadingMessages(true);
    
    try {
      const response = await WhatsAppAPI.getCustomerMessages(hotel.hotel_key, customerNumber, page);
      const { messages: newMessages, pagination, customer_info } = response.data;
      const processedMessages = newMessages.map(processMessage);
      
      if (isNewChat) {
        setSelectedChat(prev => ({
          ...prev,
          messages: processedMessages,
          name: customer_info?.customer_name || customerNumber,
          isBlocked: customer_info?.isblocked || false
        }));
        setCurrentPage(1);
        
        setTimeout(() => {
          const messagesContainer = document.querySelector('.messages-container');
          if (messagesContainer) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
          }
        }, 100);
        
        if (processedMessages.some(msg => !msg.isMe)) {
          WhatsAppAPI.markAsRead(hotel.hotel_key, customerNumber).then(() => {
            setChats(prevChats => 
              prevChats.map(chat => 
                chat.number === customerNumber ? { ...chat, unreadCount: 0 } : chat
              )
            );
          });
        }
      } else {
        setSelectedChat(prev => ({
          ...prev,
          messages: [...processedMessages, ...prev.messages]
        }));
      }
      
      setHasMoreMessages(pagination?.has_more || false);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching customer messages:", error);
      if (error.message !== 'API call already in progress') {
        errorToster("Failed to fetch messages");
      }
    } finally {
      setIsLoadingMessages(false);
    }
  }, [hotel?.hotel_key, isLoadingMessages, WhatsAppAPI]);

  const handleChatSelect = useCallback(async (chat) => {
    if (!chat) return;
    
    if (selectedChat && selectedChat.number === chat.number) {
      if (isMobile) setShowMobileChat(true);
      return;
    }
    
    setSelectedChat({ ...chat, unreadCount: 0, messages: [] });
    if (isMobile) setShowMobileChat(true);
    setCurrentPage(1);
    setHasMoreMessages(false);
    
    await loadCustomerMessages(chat.number, 1, true);
  }, [loadCustomerMessages, isMobile, selectedChat]);

  const handleSendMessage = useCallback(async (messageText = null, mediaFile = null) => {
    const textToSend = messageText || message.trim();
    if ((!textToSend && !mediaFile) || !selectedChat) return;
    
    if (mediaFile && mediaFile.size > MAX_FILE_SIZE) {
      errorToster(`File size exceeds 16MB limit. Current size: ${(mediaFile.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }
    
    const tempId = uuidv4();
    const messageType = mediaFile ? getMediaType(mediaFile.type) : 'text';
    const now = new Date();
    const timestamp = now.getTime();
    
    const optimisticMessage = {
      id: null,
      tempId: tempId,
      text: textToSend,
      time: formatDisplayTime(now), // Display format
      preciseTime: formatPreciseTime(now), // Precise format
      isMe: true,
      status: "sending",
      senderName: "You",
      timestamp: timestamp,
      messageType: messageType,
      customerNumber: selectedChat.normalizedNumber,
      mediaUrl: mediaFile ? URL.createObjectURL(mediaFile) : null,
      mediaId: null,
      filename: mediaFile?.name,
      mediaSize: mediaFile?.size
    };
    
    messageStore.current.set(tempId, {
      message: optimisticMessage,
      textToSend,
      mediaFile,
      customerNumber: selectedChat.normalizedNumber
    });
    
    setSelectedChat(prev => ({
      ...prev,
      messages: [...prev.messages, optimisticMessage]
    }));
    
    updateChatOrder(
      selectedChat.normalizedNumber,
      textToSend || getMediaDisplayText(messageType, mediaFile?.name),
      messageType,
      timestamp, // Use precise timestamp for ordering
      false
    );
    
    if (!messageText) setMessage("");
    
    try {
      let mediaData = {};
      if (mediaFile) {
        const uploadResult = await WhatsAppAPI.uploadMedia(hotel.hotel_key, mediaFile);
        if (uploadResult.results?.[0]?.media_id) {
          mediaData = {
            media_filename: uploadResult.results[0].filename,
            media_id: uploadResult.results[0].media_id,
            media_type: uploadResult.results[0].mime_type,
            s3_key: uploadResult.results[0].s3_key,
            s3_url: uploadResult.results[0].s3_url,
            media_size: uploadResult.results[0].file_size
          };
          
          setSelectedChat(prev => ({
            ...prev,
            messages: prev.messages.map(msg => 
              msg.tempId === tempId 
                ? { ...msg, mediaUrl: mediaData.s3_url, mediaId: mediaData.media_id }
                : msg
            )
          }));
        } else {
          throw new Error('Failed to get media ID from upload');
        }
      }
      
      if (socket && hotel?.hotel_key) {
        const messagePayload = {
          hotel_key: hotel.hotel_key,
          temp_id: tempId,
          message: { message: textToSend, ...mediaData },
          to: selectedChat.normalizedNumber
        };
        socket.emit("send_message", messagePayload);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setSelectedChat(prev => ({
        ...prev,
        messages: prev.messages.map(msg => 
          msg.tempId === tempId ? { ...msg, status: "failed" } : msg
        )
      }));
      errorToster(error.message || "Failed to send message");
    }
  }, [socket, hotel?.hotel_key, message, selectedChat, WhatsAppAPI, updateChatOrder]);

  const handleRetryMessage = useCallback(async (tempId) => {
    const storedData = messageStore.current.get(tempId);
    if (!storedData) return;
    
    const { textToSend, mediaFile, customerNumber } = storedData;
    
    setSelectedChat(prev => ({
      ...prev,
      messages: prev.messages.map(msg => 
        msg.tempId === tempId ? { ...msg, status: "sending" } : msg
      )
    }));
    
    try {
      let mediaData = {};
      if (mediaFile) {
        const uploadResult = await WhatsAppAPI.uploadMedia(hotel.hotel_key, mediaFile);
        if (uploadResult.results?.[0]?.media_id) {
          mediaData = {
            media_filename: uploadResult.results[0].filename,
            media_id: uploadResult.results[0].media_id,
            media_type: uploadResult.results[0].mime_type,
            s3_key: uploadResult.results[0].s3_key,
            s3_url: uploadResult.results[0].s3_url,
            media_size: uploadResult.results[0].file_size
          };
        } else {
          throw new Error('Failed to get media ID from upload');
        }
      }
      
      if (socket && hotel?.hotel_key) {
        const messagePayload = {
          hotel_key: hotel.hotel_key,
          temp_id: tempId,
          message: { message: textToSend, ...mediaData },
          to: customerNumber
        };
        socket.emit("send_message", messagePayload);
      }
    } catch (error) {
      console.error('Failed to retry message:', error);
      setSelectedChat(prev => ({
        ...prev,
        messages: prev.messages.map(msg => 
          msg.tempId === tempId ? { ...msg, status: "failed" } : msg
        )
      }));
      errorToster(error.message || "Failed to retry message");
    }
  }, [socket, hotel?.hotel_key, WhatsAppAPI]);

  const handleDeleteMessage = useCallback(async (msg) => {
    if (!selectedChat || !hotel?.hotel_key || !msg?.id) return;
    
    try {
      await WhatsAppAPI.deleteMessage(hotel.hotel_key, selectedChat.number, msg.id);
      
      setSelectedChat(prev => ({
        ...prev,
        messages: prev.messages.filter(message => message.id !== msg.id)
      }));
      
      setChats(prevChats => prevChats.map(chat => {
        if (chat.number === selectedChat.number || chat.normalizedNumber === selectedChat.normalizedNumber) {
          const currentMessages = selectedChat.messages.filter(message => message.id !== msg.id);
          const deletedMessageText = msg.text || getMediaDisplayText(msg.messageType, msg.filename);
          const isLastMessage = chat.lastMessage === deletedMessageText;
          
          if (isLastMessage && currentMessages.length > 0) {
            const sortedMessages = [...currentMessages].sort((a, b) => b.timestamp - a.timestamp);
            const newLastMessage = sortedMessages[0];
            
            return {
              ...chat,
              messages: currentMessages,
              lastMessage: newLastMessage.text || getMediaDisplayText(newLastMessage.messageType, newLastMessage.filename),
              lastMessageAt: newLastMessage.time,
              lastMessageTimestamp: newLastMessage.timestamp,
              lastMessageType: newLastMessage.messageType,
              lastMessageInbound: !newLastMessage.isMe,
              lastMessageStatus: newLastMessage.status
            };
          } else if (isLastMessage) {
            return {
              ...chat,
              messages: [],
              lastMessage: "",
              lastMessageAt: "",
              lastMessageTimestamp: null,
              lastMessageType: 'text',
              lastMessageInbound: false,
              lastMessageStatus: 'delivered'
            };
          }
          
          return { ...chat, messages: currentMessages };
        }
        return chat;
      }));
      
      successToster("Message deleted successfully");
    } catch (error) {
      console.error('Failed to delete message:', error);
      errorToster("Failed to delete message");
    }
  }, [selectedChat, hotel?.hotel_key, WhatsAppAPI]);

  const handleMediaSelect = useCallback((file) => {
    if (file.size > MAX_FILE_SIZE) {
      errorToster(`File size exceeds 16MB limit. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }
    setMediaPreview({ file, caption: "" });
  }, []);

  const handleMediaSend = useCallback((file, caption) => {
    setMediaPreview(null);
    handleSendMessage(caption, file);
  }, [handleSendMessage]);

  const handleUpdateName = useCallback(async (newName) => {
    if (!selectedChat || !hotel?.hotel_key || !newName?.trim()) return;
    
    try {
      await WhatsAppAPI.updateCustomerName(hotel.hotel_key, selectedChat.number, newName);
      
      setSelectedChat(prev => ({ ...prev, name: newName }));
      setChats(prevChats => 
        prevChats.map(chat => 
          chat.number === selectedChat.number ? { ...chat, name: newName } : chat
        )
      );
      
      successToster("Name updated successfully");
    } catch (error) {
      console.error('Failed to update name:', error);
      errorToster("Failed to update name");
    } finally {
      handleMenuClose();
    }
  }, [selectedChat, hotel?.hotel_key, WhatsAppAPI]);

  useEffect(() => {
    if (!socket || !hotel?.hotel_key) return;
    
    const handleNewMessage = (messageData) => {
      try {
        console.log(messageData);
        const processedMessage = processMessage(messageData);
        const customerNumber = processedMessage.customerNumber;
        
        if (!processedMessage.isMe) {
          updateChatOrder(
            customerNumber,
            processedMessage.text || getMediaDisplayText(processedMessage.messageType, processedMessage.filename),
            processedMessage.messageType,
            processedMessage.timestamp,
            !processedMessage.isMe
          );
          
          if (selectedChat && (selectedChat.number === customerNumber || selectedChat.normalizedNumber === customerNumber)) {
            WhatsAppAPI.markAsRead(hotel.hotel_key, customerNumber);
          }
        }
        
        setSelectedChat(prevSelected => {
          if (prevSelected && (prevSelected.number === customerNumber || prevSelected.normalizedNumber === customerNumber)) {
            const updatedMessages = [...prevSelected.messages, processedMessage];
            
            setTimeout(() => {
              const messagesContainer = document.querySelector('.messages-container');
              if (messagesContainer) {
                messagesContainer.scrollTop = messagesContainer.scrollHeight;
              }
            }, 100);
            
            return { ...prevSelected, messages: updatedMessages, unreadCount: 0 };
          }
          return prevSelected;
        });
        
        setChats(prevChats => {
          const existingChatIndex = prevChats.findIndex(chat => 
            chat.number === customerNumber || chat.normalizedNumber === customerNumber
          );
          
          const messageText = processedMessage.text || getMediaDisplayText(processedMessage.messageType, processedMessage.filename);
          
          if (existingChatIndex !== -1) {
            const existingChat = prevChats[existingChatIndex];
            const updatedChat = {
              ...existingChat,
              lastMessage: messageText,
              lastMessageAt: processedMessage.time,
              lastMessageTimestamp: processedMessage.timestamp,
              lastMessageType: processedMessage.messageType,
              lastMessageInbound: !processedMessage.isMe,
              lastMessageStatus: processedMessage.status,
              unreadCount: (selectedChat?.number === existingChat.number || selectedChat?.normalizedNumber === existingChat.normalizedNumber) 
                ? 0 : existingChat.unreadCount + (processedMessage.isMe ? 0 : 1)
            };
            
            const otherChats = prevChats.filter((_, index) => index !== existingChatIndex);
            return sortChatsByTimestamp([updatedChat, ...otherChats]);
          } else {
            const newChat = {
              id: customerNumber,
              name: processedMessage.senderName || customerNumber,
              number: customerNumber,
              normalizedNumber: normalizePhoneNumber(customerNumber),
              lastMessage: messageText,
              lastMessageAt: processedMessage.time,
              lastMessageTimestamp: processedMessage.timestamp,
              lastMessageType: processedMessage.messageType,
              lastMessageInbound: !processedMessage.isMe,
              lastMessageStatus: processedMessage.status,
              unreadCount: processedMessage.isMe ? 0 : 1,
              isBlocked: false,
              messages: []
            };
            
            return sortChatsByTimestamp([newChat, ...prevChats]);
          }
        });
      } catch (error) {
        console.error('Error handling new message:', error);
      }
    };
    
    const handleMessageSentSuccess = (data) => {
      const { temp_id, response } = data;
      const message_id = response?.messages?.[0]?.id;
      
      if (temp_id && message_id) {
        setSelectedChat(prevSelected => {
          if (!prevSelected) return prevSelected;
          return {
            ...prevSelected,
            messages: prevSelected.messages.map(msg => 
              msg.tempId === temp_id ? { ...msg, id: message_id, status: "sent" } : msg
            )
          };
        });
        
        setChats(prevChats => 
          prevChats.map(chat => 
            chat.number === selectedChat?.number ? { ...chat, lastMessageStatus: "sent" } : chat
          )
        );
        
        messageStore.current.delete(temp_id);
      }
    };
    
    const handleMessageSentError = (data) => {
      const { temp_id, error } = data;
      setSelectedChat(prevSelected => {
        if (!prevSelected) return prevSelected;
        return {
          ...prevSelected,
          messages: prevSelected.messages.map(msg => 
            msg.tempId === temp_id ? { ...msg, status: "failed" } : msg
          )
        };
      });
      errorToster(error || "Failed to send message");
    };
    
    const handleMessageStatusUpdate = (data) => {
      const { message_id, status } = data;
      if (!message_id || !status) return;
      
      setSelectedChat(prevSelected => {
        if (!prevSelected) return prevSelected;
        return {
          ...prevSelected,
          messages: prevSelected.messages.map(msg => 
            msg.id === message_id ? { ...msg, status } : msg
          )
        };
      });
      
      setChats(prevChats => 
        prevChats.map(chat => {
          const hasMessage = chat.messages?.some(msg => msg.id === message_id);
          if (hasMessage || (selectedChat && chat.number === selectedChat.number)) {
            return { ...chat, lastMessageStatus: status };
          }
          return chat;
        })
      );
    };
    
    socket.on("new_message", handleNewMessage);
    socket.on("message_sent_success", handleMessageSentSuccess);
    socket.on("message_sent_error", handleMessageSentError);
    socket.on("message_status_update", handleMessageStatusUpdate);
    
    return () => {
      socket.off("new_message", handleNewMessage);
      socket.off("message_sent_success", handleMessageSentSuccess);
      socket.off("message_sent_error", handleMessageSentError);
      socket.off("message_status_update", handleMessageStatusUpdate);
    };
  }, [socket, selectedChat, WhatsAppAPI, hotel?.hotel_key, sortChatsByTimestamp]); 

  const handleKeyPress = useCallback((e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handleSendMessage();
      }
  }, [handleSendMessage]);

  const handleBackToChats = useCallback(() => {
      setShowMobileChat(false);
      setSelectedChat(null);
  }, []);

  const handleMenuClick = useCallback((type, event) => {
      setAnchorEl(event.currentTarget);
      setMenuType(type);
  }, []);

  const handleMenuClose = useCallback(() => {
      setAnchorEl(null);
      setMenuType(null);
  }, []);

  const loadMoreMessages = useCallback(() => {
      if (!selectedChat || isLoadingMessages || !hasMoreMessages) return;
      loadCustomerMessages(selectedChat.number, currentPage + 1, false);
  }, [selectedChat, isLoadingMessages, hasMoreMessages, currentPage, loadCustomerMessages]);

  useEffect(() => {
      const customerNumber = localStorage.getItem("chat_redirect_number");
      if (customerNumber && chats.length) {
          const chat = chats.find(
              (c) => c.number === customerNumber || c.normalizedNumber === customerNumber
          );
          if (chat) {
              handleChatSelect(chat);
              localStorage.removeItem("chat_redirect_number");
          }
      }
  }, [chats, handleChatSelect]);

  const renderMainMenu = () => (
      <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl) && menuType === 'main'}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
          <MenuItem onClick={handleMenuClose}>
              <ListItemIcon><StarBorder fontSize="small" /></ListItemIcon>
              <Typography variant="inherit">Starred messages</Typography>
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              <Typography variant="inherit">Log out</Typography>
          </MenuItem>
      </Menu>
  );

  const renderChatMenu = () => (
      <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl) && menuType === 'chat'}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
          <MenuItem onClick={() => setNameEditOpen(true)}>
              <ListItemIcon><Info fontSize="small" /></ListItemIcon>
              <Typography variant="inherit">Update Name</Typography>
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>
              <ListItemIcon><Archive fontSize="small" /></ListItemIcon>
              <Typography variant="inherit">Archive chat</Typography>
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>
              <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
              <Typography variant="inherit">Delete chat</Typography>
          </MenuItem>
      </Menu>
  );

  return (
      <Paper
          elevation={0}
          sx={{
              display: "flex",
              height: { xs: "92dvh", sm: "88dvh" },
              overflow: "hidden",
              maxWidth: "100%",
              position: 'relative'
          }}
      >
          {(!isMobile || !showMobileChat) && (
              <ChatList
                  chats={chats}
                  selectedChat={selectedChat}
                  onChatSelect={handleChatSelect}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  onMenuClick={(event) => handleMenuClick('main', event)}
                  hotel={hotel}
                  isLoading={isLoadingChats}
              />
          )}

          {(!isMobile || showMobileChat) && (
              <ChatWindow
                  selectedChat={selectedChat}
                  message={message}
                  onMessageChange={setMessage}
                  onSendMessage={handleSendMessage}
                  onKeyPress={handleKeyPress}
                  onBackClick={handleBackToChats}
                  onLoadMoreMessages={loadMoreMessages}
                  hasMoreMessages={hasMoreMessages}
                  isLoadingMessages={isLoadingMessages}
                  hotel={hotel}
                  isMobile={isMobile}
                  onMenuClick={(event) => handleMenuClick('chat', event)}
                  onMediaSelect={handleMediaSelect}
                  onRetryMessage={handleRetryMessage}
                  messageStore={messageStore.current}
                  onUpdateName={handleUpdateName}
                  onDeleteMessage={handleDeleteMessage}
              />
          )}

          {mediaPreview && (
              <MediaPreview
                  file={mediaPreview.file}
                  caption={mediaPreview.caption}
                  onSend={handleMediaSend}
                  onClose={() => setMediaPreview(null)}
                  onCaptionChange={(caption) => setMediaPreview(prev => ({ ...prev, caption }))}
              />
          )}

          {/* Name Edit Dialog */}
          <NameEditDialog
            open={nameEditOpen}
            currentName={selectedChat?.name}
            onClose={() => setNameEditOpen(false)}
            onSave={(newName) => {
              handleUpdateName(newName);
              setNameEditOpen(false);
            }}
          />

          {renderMainMenu()}
          {renderChatMenu()}
      </Paper>
  );
};

export default WhatsAppWeb;